/**
 * Money is integer cents everywhere: in Postgres (bigint *_cents columns),
 * on the wire, and in every computation. Dollars exist only at the edges
 * (form inputs and formatted labels).
 */
export type Cents = number;

export function dollarsToCents(dollars: number): Cents {
  return Math.round(dollars * 100);
}

export function centsToDollars(cents: Cents): number {
  return cents / 100;
}

/** cents × pct%, rounded to the cent. */
export function applyPct(cents: Cents, pct: number): Cents {
  return Math.round((cents * pct) / 100);
}

const whole = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const exact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Whole dollars, rounded half away from zero alike either side of it, and never a negative nought. */
function wholeDollars(cents: Cents): number {
  const d = Math.round(Math.abs(cents) / 100);
  return d === 0 ? 0 : Math.sign(cents) * d;
}

/** "$1,240,000" — whole dollars, the default for anything client-facing. */
export function formatMoney(cents: Cents): string {
  return whole.format(wholeDollars(cents));
}

/** "$1,240,000.50" — unit costs and line items where cents matter. */
export function formatMoneyExact(cents: Cents): string {
  return exact.format(cents / 100);
}

/**
 * "$1.24M", "$412k", "$950" — for tight labels under the cost bar. Each size is
 * chosen after rounding, so $999,600 is "$1M", never "$1000k", and $999.70 is
 * "$1k", never "$1000".
 */
export function formatMoneyShort(cents: Cents): string {
  const dollars = cents / 100;
  const abs = Math.abs(dollars);
  // A sum that rounds to nothing has no sign.
  const sign = dollars < 0 && Math.round(abs) > 0 ? '-' : '';
  const millions = Number((abs / 1_000_000).toFixed(2));
  if (millions >= 1) return `${sign}$${trim(millions, 2)}M`;
  const thousands = Math.round(abs / 1000);
  if (abs >= 9_950 && thousands >= 10) return thousands >= 1000 ? `${sign}$1M` : `${sign}$${thousands}k`;
  const tenths = Number((abs / 1000).toFixed(1));
  if (tenths >= 1) return `${sign}$${trim(tenths, 1)}k`;
  return `${sign}$${Math.round(abs)}`;
}

function trim(n: number, digits: number): string {
  return n.toFixed(digits).replace(/\.?0+$/, '');
}

/** Parse "$1,240.50" / "1240.5" / "" from a form field into cents (null when blank). */
export function parseMoneyInput(raw: string): Cents | null {
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return dollarsToCents(n);
}
